import { FC } from 'react';

import { Button, Select, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
  paymentIncidentTypeOptions,
  paymentProviderOptions,
} from 'common/constants';
import { ICriticalIncident } from 'common/types/payment-diagnostics';
import { cents2dollars, parseDate } from 'common/utils';
import { Table } from 'components/Table';
import { observer } from 'mobx-react-lite';

import {
  attemptStatusColor,
  severityTagColor,
} from '../helpers';
import {
  PAYMENT_LOGS_PAGE_SIZE,
  PaymentLogsViewModel,
} from '../payment-logs-view-model';

import styles from './incidents-tab.module.scss';

interface IIncidentsTabProps {
  model: PaymentLogsViewModel;
}

const columns: ColumnsType<ICriticalIncident> = [
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    width: 220,
  },
  {
    title: 'Severity',
    dataIndex: 'severity',
    key: 'severity',
    width: 110,
    render: (severity) => (
      <Tag color={severityTagColor(severity)}>{severity}</Tag>
    ),
  },
  {
    title: 'Order',
    dataIndex: 'orderId',
    key: 'orderId',
    width: 90,
    render: (orderId) => orderId ?? '—',
  },
  {
    title: 'Attempt',
    dataIndex: 'attemptId',
    key: 'attemptId',
    width: 90,
    render: (attemptId) => attemptId ?? '—',
  },
  {
    title: 'Attempt status',
    dataIndex: 'attemptStatus',
    key: 'attemptStatus',
    width: 190,
    render: (status) =>
      status ? <Tag color={attemptStatusColor(status)}>{status}</Tag> : '—',
  },
  {
    title: 'Provider',
    dataIndex: 'provider',
    key: 'provider',
    width: 100,
    render: (provider) => provider ?? '—',
  },
  {
    title: 'Amount',
    dataIndex: 'expectedAmount',
    key: 'expectedAmount',
    width: 120,
    render: (amount, record) =>
      amount !== null && amount !== undefined
        ? `${cents2dollars(amount)} ${record.expectedCurrency ?? ''}`.trim()
        : '—',
  },
  {
    title: 'Count',
    dataIndex: 'occurrenceCount',
    key: 'occurrenceCount',
    width: 80,
  },
  {
    title: 'First detected',
    dataIndex: 'firstDetectedAt',
    key: 'firstDetectedAt',
    width: 170,
    render: (date) => parseDate(date),
  },
  {
    title: 'Last checked',
    dataIndex: 'lastCheckedAt',
    key: 'lastCheckedAt',
    width: 170,
    render: (date) => (date ? parseDate(date) : '—'),
  },
];

export const IncidentsTab: FC<IIncidentsTabProps> = observer(({ model }) => {
  const handleRowClick = (incident: ICriticalIncident) => {
    if (incident.attemptId) {
      model.openDiagnostics('attempt', String(incident.attemptId));

      return;
    }

    if (incident.orderId) {
      model.openDiagnostics('order', String(incident.orderId));
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        <Select
          allowClear
          value={model.incidentType}
          options={paymentIncidentTypeOptions}
          placeholder="Filter by incident type"
          disabled={model.incidentsLoading}
          className={styles.field}
          onChange={model.changeIncidentType}
        />
        <Select
          allowClear
          value={model.incidentProvider}
          options={paymentProviderOptions}
          placeholder="Filter by provider"
          disabled={model.incidentsLoading}
          className={styles.field}
          onChange={model.changeIncidentProvider}
        />
        <Button
          danger
          type="primary"
          disabled={!model.selectedIncidentIds.length || model.incidentsLoading}
          onClick={() => model.openResolveModal(model.selectedIncidentIds)}
        >
          {model.selectedIncidentIds.length
            ? `Close selected (${model.selectedIncidentIds.length})`
            : 'Close selected'}
        </Button>
      </div>
      <Table
        bordered
        rowKey="incidentId"
        columns={columns}
        data={model.incidents}
        scroll={{ x: 1340 }}
        loading={model.incidentsLoading}
        rowClassName={styles.row}
        rowSelection={{
          selectedRowKeys: model.selectedIncidentIds,
          onChange: (keys) => model.setSelectedIncidentIds(keys as number[]),
          getCheckboxProps: (incident) => ({
            disabled: incident.status !== 'OPEN',
          }),
        }}
        onRow={(incident) => ({
          onClick: (event) => {
            // The selection checkbox lives inside the row, so without this a
            // click on it would also open the diagnostics drawer.
            if (
              (event.target as HTMLElement).closest(
                '.ant-table-selection-column',
              )
            ) {
              return;
            }

            handleRowClick(incident);
          },
        })}
        pagination={{
          total: model.incidentsCount,
          current: model.incidentsPage,
          pageSize: PAYMENT_LOGS_PAGE_SIZE,
          disabled: model.incidentsLoading,
        }}
        onChange={(pagination) =>
          model.changeIncidentsPage(pagination.current ?? 1)
        }
      />
    </div>
  );
});
